import React, { useState } from 'react';
import { ListColumnType, ListFilterType } from 'types/ListTypes';
import AbstractList from 'components/lists/AbstractList';
import { useSettingsDivision } from 'components/settings/SettingsDivisionContext';
import EmailDomainForm from './EmailDomainForm';
import ListColumnCheckIcon from 'components/lists/columns/ListColumnCheckIcon';
import { fetchData } from 'services/api/fetchData';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import EmailDomainVerificationModal from './EmailDomainVerificationModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import { useGlobalContext } from 'GlobalContext';

// Rename the name of the function:
const EmailDomainList: React.FC = () => {
    const { setFloatingAlert } = useGlobalContext();
    const { handleLogout } = useAuthContext();
    const [checkingDomains, setCheckingDomains] = useState(false);

    // Filter the list on the division selected in the settings menu
    const { selectedDivision, isEnvironmentSelected } = useSettingsDivision();
    const divisionParams = !isEnvironmentSelected && selectedDivision ? { 'division_scope': selectedDivision.id } : {};

    // Specify the options of this list:
    const query = {
        endpoint: 'get_email_domain_list',
        defaultParams: { 'deleted': false, ...divisionParams },
        object: 'email_domain'
    }
    const objectName = 'email.email_domain.object_name'
    const showSearch = false
    const showDeleteOrDeactivate = 'flag_deleted'

    // Set isPaginated to true if this list may have 100.000+ items:
    const isPaginated = false

    // Specify form, detail modal or detailpage for this list
    const formModal = <EmailDomainForm />;
    const detailModal = <EmailDomainVerificationModal />;
    const onRowClick = 'detailModal'

    // Specify the columns of this list:
    const columns: ListColumnType[] = [
        { 
            width: '1.5fr', 
            field: 'domain', 
            label: 'email.email_domain.domain_label' 
        },
        {
            width: '3.5fr', 
            field: 'default_from_email',
            label: 'email.email_domain.custom_domain_from_email_label'
        },
        {
            width: '1fr',
            field: 'verified',
            label: 'email.email_domain.verified_label',
            render: (_, row) => (
                <ListColumnCheckIcon 
                    checked={row.verified} 
                    showUnchecked={true}
                    tooltipText='email.email_domain.verified_tooltipText' 
                />
            ),
            disableOrdering: true,
        }
    ];

    // Specify the filters of this list:
    const filters: ListFilterType[] = [
        {
            id: 'active',
            label: 'email.email_domain.domains_label',
            params: { 'deleted': false }
        },
        {
            id: 'inactive',
            label: 'email.email_domain.deleted_domains_label',
            params: { 'deleted': true }
        }
    ];

    return (
        <AbstractList
            key={!isEnvironmentSelected && selectedDivision ? selectedDivision.id : 'environment'}
            query={query}
            objectName={objectName}
            columns={columns}
            showSearch={showSearch}
            showDeleteOrDeactivate={showDeleteOrDeactivate}
            formModal={formModal}
            buttonSize='small'
            isPaginated={isPaginated}
            onRowClick={onRowClick}
            detailModal={detailModal}
            filters={filters}
            secondaryButton={{
                label: 'email.email_domain.check_domain_verification',
                icon: <FontAwesomeIcon icon={faArrowsRotate} />,
                loading: checkingDomains,
                onClick: async (refetchList) => {
                    // Start the button loader
                    setCheckingDomains(true);

                    try {
                        // Check the email domain verification status
                        const response = await fetchData({ 
                            apiUrl: `check_email_domain_verification`,
                            handleLogout
                        });

                        // Unpack the results
                        const results = response?.results || [];

                        // Check if any record is unverified
                        const hasUnverified = results.some((r: any) => !r.verified);

                        // Show floating alert
                        setFloatingAlert({
                            type: hasUnverified ? 'warning' : 'success',
                            message: hasUnverified
                                ? 'email.email_domain.not_verified_try_later'
                                : 'email.email_domain.verified_success'
                        });

                        // Refetch the list
                        refetchList();
                    } catch {
                        setFloatingAlert({
                            type: 'danger',
                            message: 'alert.general_error_message'
                        });
                    }

                    // Stop the button loader
                    setCheckingDomains(false);
                }
            }}
        />
    );
};

export default EmailDomainList;